import { useState } from 'react';
import {
  useAccelerateDataset,
  useDataSources,
  useRegisterDataSource,
  useSyncCatalog,
} from '../../api/admin.js';
import { useDatasets } from '../../api/datasets.js';
import { useAccelerationTierEnabled } from '../../api/capabilities.js';

/**
 * Data Sources admin.
 *
 * Register a Canopy MySQL reporting source, sync its catalog (proc
 * discovery), and accelerate individual datasets into ClickHouse.
 * Everything here is tenant-scoped; all mutations require TenantAdmin.
 */
export function DataSourcesAdminPage(): JSX.Element {
  const [showRegister, setShowRegister] = useState(false);
  const sources = useDataSources();
  const datasets = useDatasets();

  return (
    <>
      <header className="page-title">
        <div>
          <h1>Data Sources</h1>
          <p className="page-title-subtitle">
            Register connections to Canopy's reporting databases, discover their stored procedures
            as datasets, and accelerate the hot ones into ClickHouse for sub-second interactive
            queries.
          </p>
        </div>
        <div className="page-title-actions">
          <button type="button" className="btn btn--primary" onClick={() => setShowRegister(true)}>
            <PlusIcon /> Register source
          </button>
        </div>
      </header>

      <div className="stack-lg">
        <section>
          <div className="section-head">
            <div className="section-head-title">
              <h2>Sources</h2>
              <p>One row per registered data source for this tenant.</p>
            </div>
          </div>
          <div className="card">
            {sources.isLoading && <div className="card-body text-dim">Loading…</div>}
            {sources.data && sources.data.length === 0 && (
              <div className="empty-state" style={{ border: 'none' }}>
                <div className="empty-state-icon">
                  <DatabaseIcon />
                </div>
                <h3>No data sources yet</h3>
                <p>
                  Register a Canopy MySQL reporting source to start discovering stored procedures as
                  datasets.
                </p>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => setShowRegister(true)}
                >
                  Register source
                </button>
              </div>
            )}
            {sources.data && sources.data.length > 0 && (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>ID</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sources.data.map((s) => (
                    <SourceRow key={s.id} source={s} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section>
          <div className="section-head">
            <div className="section-head-title">
              <h2>Datasets</h2>
              <p>
                Procs and queries surfaced through registered sources. Accelerated datasets are
                queryable via CQS at sub-second latency.
              </p>
            </div>
            <span className="text-mute text-sm">
              {datasets.data?.length ?? 0} dataset{datasets.data?.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="card">
            {datasets.isLoading && <div className="card-body text-dim">Loading…</div>}
            {datasets.data && datasets.data.length === 0 && (
              <div className="empty-state" style={{ border: 'none' }}>
                <div className="empty-state-icon">
                  <TableIcon />
                </div>
                <h3>No datasets yet</h3>
                <p>Sync a data source's catalog to discover its stored procedures.</p>
              </div>
            )}
            {datasets.data && datasets.data.length > 0 && (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Mode</th>
                    <th>Parameters</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {datasets.data.map((d) => (
                    <DatasetRow key={d.id} dataset={d} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>

      {showRegister && <RegisterSourceDialog onClose={() => setShowRegister(false)} />}
    </>
  );
}

function SourceRow({ source }: { source: import('../../api/admin.js').DataSource }): JSX.Element {
  const sync = useSyncCatalog();
  return (
    <tr>
      <td>
        <strong>{source.name}</strong>
      </td>
      <td>
        <span className="badge badge--neutral" style={{ textTransform: 'none', letterSpacing: 0 }}>
          {source.type}
        </span>
      </td>
      <td className="cell-mono">{source.id.slice(0, 8)}…</td>
      <td className="text-dim">{new Date(source.createdAt).toLocaleString()}</td>
      <td style={{ textAlign: 'right' }}>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          disabled={sync.isPending}
          onClick={() => sync.mutate(source.id)}
        >
          {sync.isPending ? 'Syncing…' : 'Sync catalog'}
        </button>
        {sync.data && (
          <span className="text-mute text-xs" style={{ marginLeft: 8 }}>
            {sync.data.inserted > 0 && `+${sync.data.inserted} new `}
            {sync.data.updated > 0 && `${sync.data.updated} updated`}
            {sync.data.inserted === 0 && sync.data.updated === 0 && 'No changes'}
          </span>
        )}
        {sync.error && (
          <span className="text-xs" style={{ color: 'var(--color-danger)', marginLeft: 8 }}>
            Sync failed
          </span>
        )}
      </td>
    </tr>
  );
}

function DatasetRow({
  dataset,
}: {
  dataset: import('../../api/types.js').DatasetSummary;
}): JSX.Element {
  const accel = useAccelerateDataset();
  const isAccelerated = dataset.mode === 'accelerated';
  // CSAGE-437: accelerating a dataset needs the acceleration tier.
  const tierEnabled = useAccelerationTierEnabled();
  const canAccelerate = !isAccelerated && tierEnabled === true;

  const [showPicker, setShowPicker] = useState(false);
  const [sampleParams, setSampleParams] = useState<Record<string, string>>({});

  function paramsToSubmit(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const p of dataset.parameters) {
      const raw = sampleParams[p.name];
      if (raw === undefined || raw === '') {
        out[p.name] = null;
        continue;
      }
      switch (p.kind) {
        case 'integer':
          out[p.name] = parseInt(raw, 10);
          break;
        case 'float':
          out[p.name] = parseFloat(raw);
          break;
        case 'boolean':
          out[p.name] = raw === 'true';
          break;
        default:
          out[p.name] = raw;
          break;
      }
    }
    return out;
  }

  return (
    <>
      <tr>
        <td>
          <div className="col" style={{ gap: 2 }}>
            <strong>{dataset.name}</strong>
            <span className="text-mute text-xs">
              {(dataset.aiMetadata as { description?: string })?.description ?? '—'}
            </span>
          </div>
        </td>
        <td>
          {isAccelerated ? (
            <span className="badge badge--success">
              <span className="badge-dot" /> Accelerated
            </span>
          ) : (
            <span className="badge badge--neutral">Live</span>
          )}
        </td>
        <td className="text-dim text-sm">
          {dataset.parameters.length === 0 ? (
            <span className="text-mute">—</span>
          ) : (
            dataset.parameters.map((p) => p.name).join(', ')
          )}
        </td>
        <td className="text-dim">{new Date(dataset.updatedAt).toLocaleString()}</td>
        <td style={{ textAlign: 'right' }}>
          {canAccelerate && (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setShowPicker((v) => !v)}
            >
              {showPicker ? 'Cancel' : 'Accelerate…'}
            </button>
          )}
          {accel.data && (
            <span className="text-mute text-xs" style={{ marginLeft: 8 }}>
              {accel.data.rowsIngested} rows · {accel.data.durationMs} ms
            </span>
          )}
        </td>
      </tr>
      {showPicker && canAccelerate && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--color-surface-2)' }}>
            <div className="stack-sm" style={{ padding: 'var(--space-3)' }}>
              <div className="text-sm text-dim">
                Sample parameter values for the initial materialization. The proc will be executed
                once with these values; the result populates the ClickHouse cube.
              </div>
              {dataset.parameters.length === 0 ? (
                <div className="text-mute text-sm">No parameters to supply.</div>
              ) : (
                <div className="grid-2">
                  {dataset.parameters.map((p) => (
                    <label key={p.name} className="field" style={{ margin: 0 }}>
                      <span className="field-label">{p.name}</span>
                      <input
                        className="input input--sm"
                        placeholder={`${p.kind}${p.required ? ' · required' : ''}`}
                        value={sampleParams[p.name] ?? ''}
                        onChange={(e) =>
                          setSampleParams((s) => ({ ...s, [p.name]: e.target.value }))
                        }
                      />
                    </label>
                  ))}
                </div>
              )}
              <div className="row row--end">
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={accel.isPending}
                  onClick={() =>
                    accel.mutate(
                      { datasetId: dataset.id, sampleParams: paramsToSubmit() },
                      {
                        onSuccess: () => setShowPicker(false),
                      },
                    )
                  }
                >
                  {accel.isPending ? 'Accelerating…' : 'Materialize now'}
                </button>
              </div>
              {accel.error && (
                <div className="alert alert--danger">
                  <pre>
                    {JSON.stringify(
                      (accel.error as Error & { body?: unknown }).body ?? String(accel.error),
                      null,
                      2,
                    )}
                  </pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RegisterSourceDialog({ onClose }: { onClose(): void }): JSX.Element {
  const register = useRegisterDataSource();
  const [name, setName] = useState('Canopy Reporting DB');

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    await register.mutateAsync({ name, type: 'canopy_mysql_proc' });
    onClose();
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <h2>Register data source</h2>
            <div className="modal-subtitle">
              Connection config comes from the API service env vars.
            </div>
          </div>
          <button className="btn-icon" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="field">
              <label className="field-label">Type</label>
              <div className="chip chip--on" style={{ pointerEvents: 'none' }}>
                canopy_mysql_proc
              </div>
              <div className="field-help">
                The Canopy MySQL reporting database (stored procs as datasets).
              </div>
            </div>
            <div className="field">
              <label className="field-label">Display name</label>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Canopy Reporting DB"
                autoFocus
              />
            </div>
            {register.error && (
              <div className="alert alert--danger">
                <pre>
                  {JSON.stringify(
                    (register.error as Error & { body?: unknown }).body ?? String(register.error),
                    null,
                    2,
                  )}
                </pre>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={register.isPending || !name.trim()}
            >
              {register.isPending ? 'Registering…' : 'Register'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PlusIcon(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <path d="M7 3v8M3 7h8" />
    </svg>
  );
}

function DatabaseIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="10" cy="4.5" rx="6" ry="2" />
      <path d="M4 4.5v5c0 1.1 2.69 2 6 2s6-.9 6-2v-5" />
      <path d="M4 10v5c0 1.1 2.69 2 6 2s6-.9 6-2v-5" />
    </svg>
  );
}

function TableIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="14" height="14" rx="1" />
      <path d="M3 8h14" />
      <path d="M3 13h14" />
      <path d="M10 3v14" />
    </svg>
  );
}
